import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { getUserSubscription, getAiQuota, type UserSubscription, type AiQuota } from '../lib/subscriptions';
import { useAuth } from './AuthContext';
import { useImpersonate } from './ImpersonateContext';

interface SubscriptionCtx {
  subscription: UserSubscription;
  quota: AiQuota | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const defaultSubscription: UserSubscription = {
  planSlug: 'free',
  status: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  stripeSubscriptionId: null,
};

const Ctx = createContext<SubscriptionCtx>({
  subscription: defaultSubscription,
  quota: null,
  loading: true,
  refresh: async () => {},
});

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { isImpersonating, effectivePlanSlug } = useImpersonate();
  const [subscription, setSubscription] = useState<UserSubscription>(defaultSubscription);
  const [quota, setQuota] = useState<AiQuota | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setSubscription(defaultSubscription);
      setQuota(null);
      setLoading(false);
      return;
    }
    try {
      const sub = await getUserSubscription(user.id);
      setSubscription(sub);
      const q = await getAiQuota(user.id, sub.planSlug);
      setQuota(q);
    } catch {
      // silently fail — free tier defaults
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const effectiveSubscription: UserSubscription = isImpersonating
    ? { planSlug: effectivePlanSlug, status: 'active', currentPeriodEnd: null, cancelAtPeriodEnd: false, stripeSubscriptionId: null }
    : subscription;

  return (
    <Ctx.Provider value={{ subscription: effectiveSubscription, quota, loading, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export const useSubscription = () => useContext(Ctx);
